import { MessagesSquare, ClipboardCheck, Heart } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import CtaSection from "@/components/CtaSection";

const VALUES = [
  {
    icon: MessagesSquare,
    title: "Communication",
    text: "Clear, timely answers at every step — no jargon, no pressure, and no silence when families need us most.",
  },
  {
    icon: ClipboardCheck,
    title: "Organization",
    text: "Every appointment, document, transfer, and follow-up planned, confirmed, and shared with you in writing.",
  },
  {
    icon: Heart,
    title: "Compassionate support",
    text: "We work with families, not files. The practical and the emotional sides of this journey both matter to us.",
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About GetWellSoon | Healthcare Coordination for India"
        description="GetWellSoon was created to simplify the medical travel experience for international patients and families — with communication, organization, and compassionate support."
      />

      <section className="bg-pearl" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Chapter index="01" label="About us" />
          <MaskedLines
            delay={0.1}
            className="mt-8 max-w-4xl font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            lines={[
              "Coordination,",
              <span key="l2">
                done with <span className="italic text-brand">care</span>.
              </span>,
            ]}
          />
          <FadeIn delay={0.5}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              GetWellSoon was created to simplify the medical travel experience
              for patients and families seeking treatment coordination in India.
              We focus on communication, organization, and compassionate support
              throughout the journey.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeIn>
              <h2 className="font-serif text-3xl tracking-tight text-ink md:text-4xl">
                Why we exist
              </h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
                <p>
                  Considering treatment in another country is one of the
                  hardest decisions a family can face. The medical questions are
                  difficult enough — the logistics should not make them harder.
                </p>
                <p>
                  We started GetWellSoon after seeing how fragmented the
                  experience can be: hospitals to evaluate, records to send,
                  visas to arrange, flights and hotels to book, and a family to
                  keep informed — often across time zones, under real stress.
                </p>
                <p>
                  Our answer is simple: one coordinator, one clear plan, and
                  honest communication from the first conversation to the flight
                  home.
                </p>
              </div>
            </FadeIn>

            <div className="mt-14 grid gap-6 sm:grid-cols-2" data-testid="about-what-we-are">
              <FadeIn className="rounded-3xl bg-sage-light/70 p-8">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-sage-dark">
                  What we are
                </p>
                <ul className="mt-5 space-y-3 text-sm font-semibold leading-relaxed text-ink">
                  <li>Care coordinators and advocates</li>
                  <li>Travel and logistics planners</li>
                  <li>A bridge between your family and hospitals</li>
                </ul>
              </FadeIn>
              <FadeIn delay={0.12} className="rounded-3xl border border-border bg-white p-8">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                  What we are not
                </p>
                <ul className="mt-5 space-y-3 text-sm font-semibold leading-relaxed text-ink">
                  <li>Not a hospital or clinic</li>
                  <li>Not doctors or medical advisors</li>
                  <li>Never a substitute for your physician's judgment</li>
                </ul>
              </FadeIn>
            </div>
          </div>

          <div className="relative lg:col-span-6">
            <FadeIn delay={0.15}>
              <div className="overflow-hidden rounded-[2rem] rounded-tr-[5rem] border border-border">
                <img
                  src="/images/hospital-modern.jpg"
                  alt="Specialists reviewing a patient's scans and treatment plan together"
                  className="h-[340px] w-full object-cover md:h-[420px]"
                  data-testid="about-image-1"
                />
              </div>
            </FadeIn>
            <FadeIn delay={0.3} className="relative z-10 -mt-16 ml-auto w-3/4 md:-mt-24">
              <div className="overflow-hidden rounded-[2rem] rounded-bl-[4rem] border-4 border-white">
                <img
                  src="/images/consult.jpg"
                  alt="A coordinator reassuring a patient, hand in hand"
                  className="h-[220px] w-full object-cover md:h-[280px]"
                  data-testid="about-image-2"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Chapter index="02" label="How we work" />
          <MaskedLines
            as="h2"
            onView
            className="mt-8 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl"
            lines={["Three things we", "never compromise on."]}
          />
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <FadeIn
                key={v.title}
                delay={i * 0.1}
                className="group rounded-3xl border border-border bg-pearl p-10 transition-colors duration-300 hover:border-brand/40 hover:bg-white"
                data-testid={`about-value-${i}`}
              >
                <span className="font-serif text-4xl italic text-sage-light transition-colors duration-300 group-hover:text-sage">
                  0{i + 1}
                </span>
                <v.icon className="mt-6 h-6 w-6 text-brand" />
                <h3 className="mt-4 font-serif text-2xl tracking-tight text-ink">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {v.text}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
